/**
 * ShareModal — Salesforce-style sharing dialog for reports + dashboards.
 *
 * Single component reused by both surfaces. Caller passes the asset doc
 * (or just its essentials) and we drive the backend wedge-3 endpoints
 * via the useShares hooks.
 *
 * UI layout:
 *
 *   ┌────────────────────────────────────────────────┐
 *   │ Share "<asset name>"                         × │
 *   ├────────────────────────────────────────────────┤
 *   │ [Users][Groups][Roles]                          │
 *   │ ┌───────────────────────────────┐ ┌──────────┐ │
 *   │ │ Search…                       │ │ Viewer ▾ │ │
 *   │ └───────────────────────────────┘ └──────────┘ │
 *   │  ┌── suggestions ─────────────────────────────┐│
 *   │  │ ○ Ankush Tanwar  ankush.t@amroar.com  Add ││
 *   │  │ ○ Priya Sharma   priya@…              Add ││
 *   │  └────────────────────────────────────────────┘│
 *   ├────────────────────────────────────────────────┤
 *   │ Who has access                                  │
 *   │ ▢ Ankush Tanwar (Owner)                         │
 *   │ ▢ Sales Team  (Group)  [Editor ▾]  ✕            │
 *   │ ▢ Manager Role (Role)  [Viewer ▾]  ✕            │
 *   └────────────────────────────────────────────────┘
 *
 * Optimistic updates: the useShareMutations hook splices the new/edited
 * share into the cache on success so the "Who has access" list refreshes
 * immediately without waiting for the refetch round-trip.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  User, Users, Shield, Search, X, Loader2, Check, AlertTriangle, Crown,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  ACCESS_EDIT, ACCESS_MANAGE, ACCESS_READ, PRINCIPAL_GROUP, PRINCIPAL_ROLE,
  PRINCIPAL_USER, principalsApi,
} from '../api/sharesApi';
import { useAssetShares, useShareMutations } from '../hooks/useShares';

const ACCESS_LEVELS = [
  {
    value: ACCESS_READ,
    label: 'Viewer',
    description: 'Can view and run this report/dashboard',
  },
  {
    value: ACCESS_EDIT,
    label: 'Editor',
    description: 'Can also rename, change filters, and edit widgets',
  },
  {
    value: ACCESS_MANAGE,
    label: 'Manager',
    description: 'Full control — can also delete and re-share',
  },
];
const ACCESS_LABEL = ACCESS_LEVELS.reduce((m, l) => ({ ...m, [l.value]: l.label }), {});

const PRINCIPAL_TABS = [
  { value: PRINCIPAL_USER, label: 'Users', icon: User },
  { value: PRINCIPAL_GROUP, label: 'Groups', icon: Users },
  { value: PRINCIPAL_ROLE, label: 'Roles', icon: Shield },
];

const principalIcon = (type) => {
  if (type === PRINCIPAL_GROUP) return Users;
  if (type === PRINCIPAL_ROLE) return Shield;
  return User;
};

const ShareModal = ({
  open,
  onClose,
  assetType,        // 'report' | 'dashboard'
  assetId,
  assetName,
  ownerName,        // optional — surfaces an "Owner" row pinned to the top
  ownerId,
  ownerEmail,
}) => {
  const { shares, isLoading, error, refetch } = useAssetShares(assetType, assetId);
  const { grant, update, revoke } = useShareMutations(assetType, assetId);

  const [activeTab, setActiveTab] = useState(PRINCIPAL_USER);
  const [search, setSearch] = useState('');
  const [pendingAccess, setPendingAccess] = useState(ACCESS_READ);
  const [groups, setGroups] = useState([]);
  const [roles, setRoles] = useState([]);
  const [groupsRolesLoading, setGroupsRolesLoading] = useState(false);

  // Live user search (debounced). For groups/roles we load the full list
  // once and filter client-side — tenants typically have far fewer of
  // these than users, so a single fetch is cheaper than per-keystroke.
  const [userResults, setUserResults] = useState([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);

  // Reset transient state on each open so users get a clean slate.
  useEffect(() => {
    if (open) {
      setActiveTab(PRINCIPAL_USER);
      setSearch('');
      setPendingAccess(ACCESS_READ);
      setUserResults([]);
    }
  }, [open]);

  // Load groups + roles lazily — only when the modal opens, and only once
  // per session. The component instance is short-lived (mount on open),
  // so memoising at module scope wouldn't help.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setGroupsRolesLoading(true);
    Promise.all([
      principalsApi.listGroups().catch(() => []),
      principalsApi.listRoles().catch(() => []),
    ]).then(([g, r]) => {
      if (cancelled) return;
      setGroups(g);
      setRoles(r);
    }).finally(() => { if (!cancelled) setGroupsRolesLoading(false); });
    return () => { cancelled = true; };
  }, [open]);

  // Debounced user search. Skips the API call for empty/short queries so
  // we don't spam the backend with `GET /users?search=` on every keystroke.
  useEffect(() => {
    if (!open || activeTab !== PRINCIPAL_USER) return undefined;
    const q = search.trim();
    if (!q) { setUserResults([]); return undefined; }
    setUserSearchLoading(true);
    const t = setTimeout(() => {
      principalsApi.searchUsers(q, 20)
        .then((rows) => setUserResults(rows))
        .catch(() => setUserResults([]))
        .finally(() => setUserSearchLoading(false));
    }, 250);
    return () => { clearTimeout(t); setUserSearchLoading(false); };
  }, [search, activeTab, open]);

  // Existing-share fast-lookup keyed by `${type}:${id}`. Used to dim
  // already-shared rows in the suggestions list and to gate the Add button
  // (we still send the request — backend treats grant as idempotent — but
  // visual feedback that they're already shared with this principal helps).
  const alreadySharedKeys = useMemo(() => {
    const set = new Set();
    (shares || []).forEach((s) => {
      set.add(`${s.shared_with_type}:${s.shared_with_id}`);
    });
    if (ownerId) set.add(`${PRINCIPAL_USER}:${ownerId}`);
    return set;
  }, [shares, ownerId]);

  // Suggestions for the active tab. For users we render the live search
  // results; for groups/roles we filter the cached list by the same query.
  const suggestions = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (activeTab === PRINCIPAL_USER) return userResults;
    const source = activeTab === PRINCIPAL_GROUP ? groups : roles;
    if (!q) return source;
    return source.filter((row) => {
      const hay = `${row.label || ''} ${row.sublabel || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [activeTab, userResults, groups, roles, search]);

  const handleAdd = useCallback(async (principal) => {
    try {
      await grant.mutate({
        principalType: principal.type,
        principalId: principal.id,
        accessLevel: pendingAccess,
      });
      toast.success(`Shared with ${principal.label}`);
      setSearch('');
    } catch (err) {
      toast.error(err?.message || 'Could not share');
    }
  }, [grant, pendingAccess]);

  const handleChangeAccess = useCallback(async (share, accessLevel) => {
    if (share.access_level === accessLevel) return;
    try {
      await update.mutate({ shareId: share.id, accessLevel });
      toast.success(`Updated to ${ACCESS_LABEL[accessLevel]}`);
    } catch (err) {
      toast.error(err?.message || 'Could not update access');
    }
  }, [update]);

  const handleRevoke = useCallback(async (share) => {
    try {
      await revoke.mutate(share.id);
      toast.success('Access removed');
    } catch (err) {
      toast.error(err?.message || 'Could not remove access');
    }
  }, [revoke]);

  const ownerRow = ownerName || ownerId ? (
    <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-amber-50/40">
      <div className="p-1.5 rounded-full bg-amber-100">
        <Crown className="h-3.5 w-3.5 text-amber-600" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-slate-900 truncate">{ownerName || 'Owner'}</div>
        {ownerEmail && (
          <div className="text-xs text-slate-500 truncate">{ownerEmail}</div>
        )}
      </div>
      <Badge variant="outline" className="text-[10px] uppercase tracking-wide bg-amber-50 border-amber-200 text-amber-700">
        Owner
      </Badge>
    </div>
  ) : null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl p-0 overflow-hidden gap-0">
        <DialogHeader className="px-6 py-4 border-b border-slate-200">
          <DialogTitle className="text-lg text-slate-900 flex items-center gap-2 truncate">
            Share <span className="font-normal text-slate-500 truncate">"{assetName || 'this item'}"</span>
          </DialogTitle>
          <DialogDescription className="text-xs">
            Grant access to users, groups, or roles. Owners and Managers can re-share.
          </DialogDescription>
        </DialogHeader>

        {/* ── Tabs + search + access selector ───────────────────────────── */}
        <div className="px-6 pt-4 pb-2">
          <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1 w-fit mb-3">
            {PRINCIPAL_TABS.map((tab) => {
              const Icon = tab.icon;
              const active = tab.value === activeTab;
              return (
                <button
                  key={tab.value}
                  type="button"
                  onClick={() => { setActiveTab(tab.value); setSearch(''); }}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md transition-colors ${
                    active
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {tab.label}
                </button>
              );
            })}
          </div>

          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={
                  activeTab === PRINCIPAL_USER ? 'Search users by name or email…'
                    : activeTab === PRINCIPAL_GROUP ? 'Search groups…'
                      : 'Search roles…'
                }
                className="pl-8 h-9"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  aria-label="Clear search"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            <AccessLevelDropdown
              value={pendingAccess}
              onChange={setPendingAccess}
              triggerLabel={`As ${ACCESS_LABEL[pendingAccess]}`}
            />
          </div>
        </div>

        {/* ── Suggestion list ───────────────────────────────────────────── */}
        <div className="px-6 pb-3 max-h-44 overflow-y-auto">
          <SuggestionList
            activeTab={activeTab}
            suggestions={suggestions}
            isLoading={
              activeTab === PRINCIPAL_USER ? userSearchLoading : groupsRolesLoading
            }
            search={search}
            alreadySharedKeys={alreadySharedKeys}
            onAdd={handleAdd}
            pendingAccess={pendingAccess}
            adding={grant.isPending}
          />
        </div>

        {/* ── Current shares ────────────────────────────────────────────── */}
        <div className="border-t border-slate-200 px-6 py-4 max-h-[320px] overflow-y-auto">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Who has access
            </h4>
            <span className="text-[11px] text-slate-400">
              {shares.length} share{shares.length === 1 ? '' : 's'}
            </span>
          </div>

          <div className="space-y-1.5">
            {ownerRow}

            {isLoading && !shares.length && (
              <div className="flex items-center justify-center py-6 text-xs text-slate-400">
                <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" /> Loading shares…
              </div>
            )}
            {error && !shares.length && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-rose-50 text-rose-700 text-xs">
                <AlertTriangle className="h-3.5 w-3.5" />
                {error.message || 'Could not load shares'}
                <Button variant="ghost" size="sm" onClick={refetch} className="ml-auto h-6 text-xs">
                  Retry
                </Button>
              </div>
            )}
            {!isLoading && !shares.length && !error && (
              <div className="text-xs text-slate-400 px-3 py-3">
                Only the owner has access. Use the search above to share with others.
              </div>
            )}

            {shares.map((share) => (
              <ShareRow
                key={share.id}
                share={share}
                onChangeAccess={(level) => handleChangeAccess(share, level)}
                onRevoke={() => handleRevoke(share)}
                changing={update.isPending}
                revoking={revoke.isPending}
              />
            ))}
          </div>
        </div>

        <div className="px-6 py-3 border-t border-slate-200 bg-slate-50 flex justify-end">
          <Button variant="outline" onClick={onClose} size="sm">Done</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ─── Sub-components ────────────────────────────────────────────────────────

const AccessLevelDropdown = ({ value, onChange, triggerLabel, disabled }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button
        size="sm"
        variant="outline"
        disabled={disabled}
        className="h-9 min-w-[110px] justify-between gap-2"
      >
        {triggerLabel}
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="w-64">
      {ACCESS_LEVELS.map((opt, i) => (
        <React.Fragment key={opt.value}>
          {i > 0 && <DropdownMenuSeparator />}
          <DropdownMenuItem
            onClick={() => onChange(opt.value)}
            className="flex items-start gap-2 py-2 cursor-pointer"
          >
            <div className="pt-0.5 w-4">
              {opt.value === value && <Check className="h-3.5 w-3.5 text-indigo-600" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-slate-900">{opt.label}</div>
              <div className="text-xs text-slate-500">{opt.description}</div>
            </div>
          </DropdownMenuItem>
        </React.Fragment>
      ))}
    </DropdownMenuContent>
  </DropdownMenu>
);

const SuggestionList = ({
  activeTab, suggestions, isLoading, search, alreadySharedKeys, onAdd, adding,
}) => {
  const empty = !isLoading && suggestions.length === 0;
  const placeholder = activeTab === 'user' && !search
    ? 'Start typing to search users.'
    : 'No matches.';

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-4 text-xs text-slate-400">
        <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" />
        Searching…
      </div>
    );
  }
  if (empty) {
    return <div className="text-xs text-slate-400 px-1 py-2">{placeholder}</div>;
  }

  return (
    <div className="space-y-0.5">
      {suggestions.map((p) => {
        const key = `${p.type}:${p.id}`;
        const alreadyShared = alreadySharedKeys.has(key);
        const Icon = principalIcon(p.type);
        return (
          <div
            key={key}
            className={`flex items-center gap-3 px-2 py-1.5 rounded-md hover:bg-slate-50 ${
              alreadyShared ? 'opacity-60' : ''
            }`}
          >
            <div className="p-1.5 rounded-full bg-slate-100">
              <Icon className="h-3.5 w-3.5 text-slate-600" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm text-slate-900 truncate">{p.label}</div>
              {p.sublabel && (
                <div className="text-[11px] text-slate-500 truncate">{p.sublabel}</div>
              )}
            </div>
            <Button
              size="sm"
              variant={alreadyShared ? 'ghost' : 'outline'}
              disabled={adding || alreadyShared}
              onClick={() => onAdd(p)}
              className="h-7 text-xs"
            >
              {alreadyShared ? 'Shared' : 'Add'}
            </Button>
          </div>
        );
      })}
    </div>
  );
};

const ShareRow = ({ share, onChangeAccess, onRevoke, changing, revoking }) => {
  const Icon = principalIcon(share.shared_with_type);
  const isExpiring = share.expires_at;

  return (
    <div className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-slate-50">
      <div className="p-1.5 rounded-full bg-slate-100">
        <Icon className="h-3.5 w-3.5 text-slate-600" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-slate-900 truncate flex items-center gap-2">
          {share.shared_with_name || share.shared_with_id}
          <span className="text-[10px] uppercase tracking-wide text-slate-400">
            {share.shared_with_type}
          </span>
        </div>
        <div className="text-[11px] text-slate-500 truncate">
          Shared by {share.shared_by_name || 'someone'}
          {isExpiring && (
            <> · expires {new Date(share.expires_at).toLocaleDateString()}</>
          )}
        </div>
      </div>

      <AccessLevelDropdown
        value={share.access_level}
        onChange={onChangeAccess}
        triggerLabel={ACCESS_LABEL[share.access_level] || share.access_level}
        disabled={changing}
      />

      <button
        type="button"
        onClick={onRevoke}
        disabled={revoking}
        title="Remove access"
        className="p-1.5 rounded-md text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
};

export default ShareModal;
